import GuideLayout from '../../layouts/GuideLayout'
import type { TocItem } from '../../layouts/GuideLayout'
import { AffiliateCTA } from '../../components/content/AffiliateCTA'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

const tocItems: TocItem[] = [
  { id: 'why-firewall-costs-surprise-teams', label: 'Why Firewall Costs Surprise Teams', level: 2 },
  { id: 'basic-vs-standard-vs-premium', label: 'Basic vs Standard vs Premium', level: 2 },
  { id: 'logging-costs', label: 'Logging Costs', level: 2 },
  { id: 'what-to-validate', label: 'What to Validate Before You Buy', level: 2 },
  { id: 'calculator', label: 'Firewall Calculator', level: 2 },
]

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-700 Designing and Implementing Microsoft Azure Networking Solutions"
    url="https://www.pluralsight.com/paths/az-700-designing-and-implementing-microsoft-azure-networking-solutions"
    price="From £29/month"
    badgeText="Firewall · Networking · Security"
  />
)

export default function AzureFirewallPricingGuide() {
  return (
    <GuideLayout
      title="Azure Firewall Pricing Guide UK 2026"
      description="Azure Firewall looks straightforward until diagnostic logs, Log Analytics retention, and per-GB processing are added to the picture. This guide focuses on the real cost drivers for UK South in GBP."
      lastUpdated="April 2026"
      canonical="https://www.azure-calc.co.uk/guides/azure-firewall-pricing-uk"
      metaTitle="Azure Firewall Pricing Guide UK 2026 | AzureCalc.uk"
      metaDescription="Understand Azure Firewall Basic, Standard, and Premium pricing in GBP for UK South, including deployment fees, per-GB processing, and Log Analytics logging cost." 
      tocItems={tocItems}
      relatedCalculators={[
        { service: 'firewall', path: '/firewall', label: 'Azure Firewall Calculator' },
        { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
      ]}
      bottomCta={bottomCta}
    >
      <p>
        Built and maintained by an independent Azure engineer. The goal of this guide is not to list every meter Microsoft publishes. It is to explain the billing pattern that most often catches teams off guard: <strong>Azure Firewall logging can become a larger cost driver than the firewall deployment itself.</strong>
      </p>

      <h2 id="why-firewall-costs-surprise-teams">Why Firewall Costs Surprise Teams</h2>
      <p>
        Most engineers notice the hourly deployment line first. That makes sense because it is visible immediately and it looks like the product's main cost. In practice, Azure Firewall spend usually comes from two places: the standing deployment charge and the amount of traffic you push through it every day.
      </p>
      <p>
        The second surprise is operational rather than architectural. Once security teams enable diagnostic settings, the logs leave Azure Firewall and start billing again in Log Analytics. That means a "secure by default" implementation often adds a second cost centre the original estimate did not include.
      </p>

      <div className="bg-[#e8b000]/10 border border-[#e8b000]/40 p-4 flex gap-3 items-start rounded-sm my-4">
        <span className="material-symbols-outlined text-[#e8b000]">warning</span>
        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
          <strong className="text-[#e8b000]">Common planning mistake:</strong> comparing Firewall SKUs on deployment price only. At higher traffic volumes, Basic's per-GB processing rate can erase the apparent saving very quickly.
        </p>
      </div>

      <h2 id="basic-vs-standard-vs-premium">Basic vs Standard vs Premium</h2>
      <p>
        <strong>Basic</strong> is attractive for lighter estates because the deployment fee is materially lower, but it carries a much higher data processing rate. That makes it a good fit only when traffic volume stays modest.
      </p>
      <p>
        <strong>Standard</strong> is the practical baseline for many production estates. The deployment fee is higher than Basic, but the per-GB rate is far lower. Once daily processed traffic climbs, Standard often becomes the cheaper option overall.
      </p>
      <p>
        <strong>Premium</strong> is a security-feature decision as much as a pricing decision. If you need the advanced inspection capabilities, the higher fixed cost is justified. If you do not, Premium is difficult to defend as a default.
      </p>

      <h2 id="logging-costs">Logging Costs</h2>
      <p>
        This is the line item most quick estimates miss. Azure Firewall diagnostic logs are small per connection, but the volume adds up. A useful working assumption is that <strong>100 GB/day of traffic can produce roughly 7.5 GB/month of Firewall logs</strong> once ingested into Log Analytics.
      </p>
      <p>
        At current UK South PAYG rates, that is roughly £16.25/month in Log Analytics charges. In smaller Basic environments the log bill can be a large percentage of the Firewall cost. In noisy estates, it can exceed it.
      </p>

      <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 p-4 flex gap-3 items-start rounded-sm my-4">
        <span className="material-symbols-outlined text-[#0078d4]">info</span>
        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
          The important question is not "should I log?" It is "which categories, retention windows, and query patterns actually matter?" Logging everything forever is rarely the most useful or cost-effective design.
        </p>
      </div>

      <h2 id="what-to-validate">What to Validate Before You Buy</h2>
      <ul>
        <li><strong>Daily traffic volume:</strong> the Basic vs Standard break-even moves quickly once you process more data.</li>
        <li><strong>Hours per month:</strong> pilot or dev environments may not need 24×7 deployment.</li>
        <li><strong>Diagnostic categories:</strong> log only what your platform, SOC, or compliance process truly uses.</li>
        <li><strong>Retention strategy:</strong> Firewall logs without a retention plan simply shift the spend into Log Analytics.</li>
        <li><strong>Feature need:</strong> Premium only makes sense if you will use Premium capabilities in production.</li>
      </ul>

      <h2 id="calculator">Firewall Calculator</h2>
      <p>
        Use the calculator below to compare Basic, Standard, and Premium under the same traffic and deployment assumptions. It also estimates the likely Log Analytics cost of Azure Firewall logs and links you directly to the Log Analytics calculator with the derived ingestion volume pre-filled.
      </p>

      <div className="my-6">
        <CalculatorEmbed service="firewall" />
      </div>

      <p>
        When you finish comparing SKUs, go back to the <a href="/firewall" className="text-[#0078d4] hover:underline">Azure Firewall Cost Calculator</a> and test the same scenario with logging disabled versus enabled. That comparison is usually the fastest way to understand whether your logging design needs more attention than the firewall SKU itself.
      </p>
    </GuideLayout>
  )
}
